import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Shield,
  Lock,
  ArrowRight,
  ArrowLeft,
  KeyRound,
  Mail,
  CheckCircle2,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useSuperAdmin } from "./SuperAdminContext";
import { api, getErrorMessage } from "../../lib/api";

export default function SuperAdminLogin() {
  const { login, authenticated, setupComplete, loading } = useSuperAdmin();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // 2-Factor Password Recovery ("Forgot Password?") state
  const [mode, setMode] = useState("login"); // "login" | "forgot-request" | "forgot-confirm"
  const [resetIdentifier, setResetIdentifier] = useState("");
  const [resetMeta, setResetMeta] = useState(null);
  const [emailOtp, setEmailOtp] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  React.useEffect(() => {
    if (!loading) {
      if (!setupComplete) {
        navigate("/super-admin/setup", { replace: true });
      } else if (authenticated) {
        navigate("/super-admin", { replace: true });
      }
    }
  }, [loading, setupComplete, authenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Please enter both username and password");
      return;
    }
    setBusy(true);
    try {
      await login(username.trim(), password);
      toast.success("Super Admin authenticated");
      navigate("/super-admin", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to authenticate";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleRequestResetOtp = async (e) => {
    if (e) e.preventDefault();
    const ident = (resetIdentifier || username).trim();
    if (!ident) {
      toast.error("Please enter your Super Admin username or recovery email");
      return;
    }
    setResetBusy(true);
    try {
      const res = await api.post("/super-admin/forgot-password/request", {
        identifier: ident,
      });
      setResetIdentifier(ident);
      setResetMeta(res.data);
      setMode("forgot-confirm");
      toast.success(`6-digit verification code sent to ${res.data.masked_email}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to request verification code"));
    } finally {
      setResetBusy(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (!emailOtp.trim() || emailOtp.trim().length < 6) {
      toast.error("Please enter the 6-digit email verification code");
      return;
    }
    if (!recoveryCode.trim()) {
      toast.error("Please enter your Master Recovery Key or a Backup Code");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setResetBusy(true);
    try {
      const res = await api.post("/super-admin/forgot-password/confirm", {
        identifier: resetIdentifier.trim(),
        email_otp: emailOtp.trim(),
        recovery_code: recoveryCode.trim(),
        new_password: newPassword,
      });
      toast.success(res.data?.message || "Super Admin password reset! Please sign in.");
      if (resetMeta?.username) {
        setUsername(resetMeta.username);
      }
      setPassword("");
      setEmailOtp("");
      setRecoveryCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      setMode("login");
    } catch (err) {
      toast.error(getErrorMessage(err, "Password reset verification failed"));
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex flex-col justify-between font-sans selection:bg-teal-500 selection:text-white">
      {/* Top Banner */}
      <div className="border-b border-slate-800/80 bg-[#0E1526]/60 backdrop-blur px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-teal-500 to-emerald-400 flex items-center justify-center shadow-lg shadow-teal-500/20">
            <Shield className="w-5 h-5 text-slate-950 font-bold" />
          </div>
          <div>
            <div className="text-[11px] font-bold tracking-[0.25em] text-teal-400 uppercase">
              Rental Tracker
            </div>
            <div className="text-sm font-semibold tracking-wide text-white">
              Mission Control • Platform Operations
            </div>
          </div>
        </div>

        <Link
          to="/login"
          className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Tenant Portal</span>
        </Link>
      </div>

      {/* Main Form Center */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-[#111827]/90 border border-slate-800/80 rounded-2xl p-8 shadow-2xl backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-emerald-400 to-cyan-500" />

            {mode === "login" && (
              <>
                <div className="mb-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 text-teal-400 mb-3 shadow-inner">
                    <Lock className="w-6 h-6" />
                  </div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">Super Admin Access</h1>
                  <p className="text-xs text-slate-400 mt-2">
                    Restricted portal for multi-tenant oversight, mail server setup, and server telemetry.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                      Super Admin Username
                    </label>
                    <input
                      type="text"
                      required
                      autoFocus
                      data-testid="superadmin-login-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="e.g. root"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Master Password
                      </label>
                      <button
                        type="button"
                        data-testid="superadmin-forgot-password-button"
                        onClick={() => {
                          setResetIdentifier(username);
                          setMode("forgot-request");
                        }}
                        className="text-xs font-semibold text-teal-400 hover:text-teal-300 transition-colors"
                      >
                        Forgot Password?
                      </button>
                    </div>
                    <input
                      type="password"
                      required
                      data-testid="superadmin-login-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="superadmin-login-submit"
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-sm tracking-wide shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2 transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                          Authenticating...
                        </span>
                      ) : (
                        <>
                          <span>Enter Mission Control</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}

            {mode === "forgot-request" && (
              <>
                <div className="mb-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-400 mb-3 shadow-inner">
                    <KeyRound className="w-6 h-6" />
                  </div>
                  <h1 className="text-xl font-bold tracking-tight text-white">
                    2-Factor Password Recovery
                  </h1>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                    Resetting the Super Admin password requires <strong>both</strong> a 6-digit Email OTP and your <strong>Master Recovery Key</strong> (or a Single-Use Backup Code).
                  </p>
                </div>

                <form onSubmit={handleRequestResetOtp} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                      Super Admin Username or Email
                    </label>
                    <input
                      type="text"
                      required
                      autoFocus
                      data-testid="superadmin-reset-identifier"
                      value={resetIdentifier}
                      onChange={(e) => setResetIdentifier(e.target.value)}
                      placeholder="e.g. root or admin@yourdomain.com"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all"
                    />
                  </div>

                  <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 text-[11px] text-slate-400 space-y-1">
                    <div className="text-slate-200 font-semibold flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-teal-400" />
                      <span>Dual-Verification Security</span>
                    </div>
                    <p>
                      Step 1 sends a 10-minute verification code to your registered Super Admin email. Step 2 verifies that code alongside your offline Master Recovery Key.
                    </p>
                  </div>

                  <div className="pt-1 space-y-2.5">
                    <button
                      type="submit"
                      disabled={resetBusy}
                      data-testid="superadmin-send-otp-button"
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-sm tracking-wide shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    >
                      {resetBusy ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                          Sending Verification Code...
                        </span>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          <span>Send 6-Digit Email Code</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="w-full py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 text-xs font-semibold transition-colors"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              </>
            )}

            {mode === "forgot-confirm" && (
              <>
                <div className="mb-5 text-center">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-500/10 border border-teal-500/25 text-teal-400 mb-2.5 shadow-inner">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <h1 className="text-xl font-bold tracking-tight text-white">
                    Verify Both Security Factors
                  </h1>
                  <p className="text-xs text-slate-400 mt-1">
                    Code sent to <span className="text-teal-300 font-semibold">{resetMeta?.masked_email}</span> (valid for 10 min)
                  </p>
                </div>

                <form onSubmit={handleConfirmReset} className="space-y-3.5">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-teal-400">
                        Factor 1 • 6-Digit Email Code
                      </label>
                      <button
                        type="button"
                        onClick={handleRequestResetOtp}
                        disabled={resetBusy}
                        className="text-[11px] text-slate-400 hover:text-teal-300 inline-flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        <span>Resend</span>
                      </button>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="superadmin-reset-otp-input"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white font-mono tracking-[0.35em] text-center text-base focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] font-bold uppercase tracking-wider text-amber-400">
                        Factor 2 • Master Recovery Key or Backup Code
                      </label>
                    </div>
                    <input
                      type="text"
                      required
                      data-testid="superadmin-reset-recovery-key-input"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value)}
                      placeholder={
                        resetMeta?.recovery_key_hint
                          ? `e.g. ${resetMeta.recovery_key_hint} or XXXX-XXXX`
                          : "RT-SA-XXXX-XXXX-XXXX-XXXX or XXXX-XXXX"
                      }
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white font-mono text-xs uppercase placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      {resetMeta?.recovery_key_hint && (
                        <span>Key hint: <code className="text-slate-300">{resetMeta.recovery_key_hint}</code></span>
                      )}
                      {resetMeta?.unused_backup_codes > 0 && (
                        <span> • {resetMeta.unused_backup_codes} single-use backup code(s) available</span>
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                    <div>
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        required
                        data-testid="superadmin-reset-new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 6 chars"
                        className="w-full px-3.5 py-2 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        required
                        data-testid="superadmin-reset-confirm-password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Repeat password"
                        className="w-full px-3.5 py-2 rounded-xl bg-slate-900/90 border border-slate-700/80 text-white text-xs focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                      />
                    </div>
                  </div>

                  <div className="pt-2 space-y-2">
                    <button
                      type="submit"
                      disabled={resetBusy}
                      data-testid="superadmin-reset-confirm-submit"
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-xs tracking-wide shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    >
                      {resetBusy ? (
                        <span>Verifying & Resetting...</span>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Verify Both Factors & Reset Password</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="w-full py-2 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-400 text-xs font-semibold transition-colors"
                    >
                      Cancel & Return to Sign In
                    </button>
                  </div>
                </form>
              </>
            )}

            <div className="mt-6 pt-4 border-t border-slate-800 text-center">
              <p className="text-[11px] text-slate-500">
                Unauthorized access is strictly monitored and logged via security audit trails.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-4 text-center text-xs text-slate-600 border-t border-slate-900">
        Rental Tracker Platform Operations • Super Admin Mission Control
      </div>
    </div>
  );
}
